import { useState } from "react";
import styled from "styled-components";
import { STUDIOS } from "shared";
import { NavbarProvider } from "../../navigation/components/NavbarProvider";
import { mediaMobile } from "../../theme/constants/queries";
import type { DayMetrics } from "../hooks/useMetrics";
import { useMetrics } from "../hooks/useMetrics";

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

const Page = styled.div`
  padding: 32px 24px;
  max-width: 900px;
  margin: 0 auto;

  ${mediaMobile`
    padding: 20px 16px;
  `}
`;

const Heading = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${(p) => p.theme.colors.main};
  margin: 0 0 28px;
`;

const Section = styled.section`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${(p) => p.theme.colors.secondary};
  margin: 0 0 16px;
`;

const TotalsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 40px;

  ${mediaMobile`
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  `}
`;

const TotalCard = styled.div`
  background: ${(p) => p.theme.colors.mainSurface};
  border: 1px solid ${(p) => p.theme.borderColor};
  border-radius: ${(p) => p.theme.borderRadius};
  padding: 16px 20px;
`;

const TotalLabel = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme.colors.secondary};
  margin-bottom: 4px;
`;

const TotalValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${(p) => p.theme.colors.main};
`;

const StatusMessage = styled.p`
  color: ${(p) => p.theme.colors.secondary};
  font-size: 14px;
`;

// ---------------------------------------------------------------------------
// Chart shared primitives
// ---------------------------------------------------------------------------

// Coordinate space — X stretches to fill any container, Y is fixed.
const VB_W = 1000;
const VB_H = 140;
const TOP_PAD = 12;
const BOT_PAD = 4;
const PLOT_H = VB_H - TOP_PAD - BOT_PAD;

function xAt(i: number, n: number): number {
  return n <= 1 ? VB_W / 2 : (i / (n - 1)) * VB_W;
}

function yAt(value: number, maxValue: number): number {
  return TOP_PAD + PLOT_H * (1 - value / maxValue);
}

const GRID_FRACTIONS = [0.25, 0.5, 0.75];

const ChartOuter = styled.div`
  background: ${(p) => p.theme.colors.mainSurface};
  border: 1px solid ${(p) => p.theme.borderColor};
  border-radius: ${(p) => p.theme.borderRadius};
  padding: 16px 16px 12px;
`;

const ChartSvg = styled.svg`
  display: block;
  width: 100%;
  height: 140px;
  /* currentColor is used by grid lines so they inherit the theme secondary color */
  color: ${(p) => p.theme.colors.secondary};
`;

const DayLabelsRow = styled.div`
  display: flex;
  margin-top: 6px;
`;

const DayLabelCell = styled.div`
  flex: 1;
  text-align: center;
  font-size: 10px;
  color: ${(p) => p.theme.colors.secondary};
  overflow: hidden;
`;

const Legend = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${(p) => p.theme.colors.secondary};
`;

const LegendDot = styled.div<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background-color: ${(p) => p.$color};
  flex-shrink: 0;
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function shortDate(iso: string): string {
  const [, month, day] = iso.split("-");
  return `${parseInt(month)}/${parseInt(day)}`;
}

interface SeriesSpec {
  value: number;
  color: string;
  label: string;
}

function ChartFooter({
  days,
  legend,
}: {
  days: DayMetrics[];
  legend: { color: string; label: string }[];
}) {
  return (
    <>
      <DayLabelsRow>
        {days.map((day) => (
          <DayLabelCell key={day.date}>{shortDate(day.date)}</DayLabelCell>
        ))}
      </DayLabelsRow>
      <Legend>
        {legend.map((item) => (
          <LegendItem key={item.label}>
            <LegendDot $color={item.color} />
            {item.label}
          </LegendItem>
        ))}
      </Legend>
    </>
  );
}

// ---------------------------------------------------------------------------
// Interactive overlay (tooltip + dots)
// ---------------------------------------------------------------------------

const SvgWrapper = styled.div`
  position: relative;
`;

const Tooltip = styled.div<{ $pct: number }>`
  position: absolute;
  top: 6px;
  /* clamp keeps the box within the chart edges */
  left: clamp(0px, calc(${(p) => p.$pct}% - 54px), calc(100% - 108px));
  background: ${(p) => p.theme.colors.mainSurface};
  border: 1px solid ${(p) => p.theme.borderColor};
  border-radius: ${(p) => p.theme.borderRadius};
  padding: 6px 10px;
  pointer-events: none;
  z-index: 10;
  min-width: 108px;
`;

const TooltipDate = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${(p) => p.theme.colors.main};
  margin-bottom: 4px;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${(p) => p.theme.colors.secondary};
  line-height: 1.6;
`;

const TooltipDot = styled.div<{ $color: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  flex-shrink: 0;
`;

const TooltipValue = styled.span`
  margin-left: auto;
  font-weight: 600;
  color: ${(p) => p.theme.colors.main};
`;

// HTML dot — avoids SVG ellipse distortion from non-uniform preserveAspectRatio
const HoverDot = styled.div<{ $color: string }>`
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  border: 2px solid ${(p) => p.theme.colors.mainSurface};
  transform: translate(-50%, -50%);
  pointer-events: none;
`;

// ---------------------------------------------------------------------------
// Line chart
// ---------------------------------------------------------------------------
// Each series is drawn as a stroked path with a light filled area underneath.
// Used for independent metrics that shouldn't be stacked.

function LineChart({
  days,
  seriesForDay,
  legend,
}: {
  days: DayMetrics[];
  seriesForDay: (day: DayMetrics) => SeriesSpec[];
  legend: { color: string; label: string }[];
}) {
  const n = days.length;
  const [activeDay, setActiveDay] = useState<number | null>(null);

  const allSeries = days.map(seriesForDay);
  const maxValue = Math.max(
    ...allSeries.flatMap((s) => s.map((v) => v.value)),
    1
  );
  const seriesCount = allSeries[0].length;
  const baseY = yAt(0, maxValue).toFixed(1);

  function pickDay(clientX: number, target: SVGSVGElement): number {
    const { left, width } = target.getBoundingClientRect();
    const frac = (clientX - left) / width;
    return Math.max(0, Math.min(n - 1, Math.round(frac * (n - 1))));
  }

  // X position of activeDay as a percentage of the SVG width (0–100)
  const activePct =
    activeDay !== null ? (activeDay / (n - 1)) * 100 : null;

  return (
    <ChartOuter>
      <SvgWrapper>
        {activeDay !== null && activePct !== null && (
          <>
            <Tooltip $pct={activePct}>
              <TooltipDate>{days[activeDay].date}</TooltipDate>
              {allSeries[activeDay].map((s) => (
                <TooltipRow key={s.label}>
                  <TooltipDot $color={s.color} />
                  {s.label}
                  <TooltipValue>{s.value}</TooltipValue>
                </TooltipRow>
              ))}
            </Tooltip>
            {allSeries[activeDay].map((s, si) => (
              <HoverDot
                key={si}
                $color={s.color}
                style={{
                  left: `${activePct}%`,
                  // yAt maps to viewBox coords 0–VB_H; SVG CSS height = VB_H px
                  top: `${(yAt(s.value, maxValue) / VB_H) * 100}%`,
                }}
              />
            ))}
          </>
        )}
        <ChartSvg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          preserveAspectRatio="none"
          aria-hidden="true"
          style={{ cursor: "crosshair", display: "block" }}
          onMouseMove={(e) => setActiveDay(pickDay(e.clientX, e.currentTarget))}
          onMouseLeave={() => setActiveDay(null)}
          onTouchStart={(e) =>
            setActiveDay(pickDay(e.touches[0].clientX, e.currentTarget))
          }
          onTouchMove={(e) =>
            setActiveDay(pickDay(e.touches[0].clientX, e.currentTarget))
          }
          onTouchEnd={() => setActiveDay(null)}
        >
          {GRID_FRACTIONS.map((f) => {
            const y = (TOP_PAD + PLOT_H * (1 - f)).toFixed(1);
            return (
              <line
                key={f}
                x1={0}
                y1={y}
                x2={VB_W}
                y2={y}
                stroke="currentColor"
                strokeOpacity={0.1}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}

          {/* Vertical crosshair */}
          {activeDay !== null && (
            <line
              x1={xAt(activeDay, n).toFixed(1)}
              y1={TOP_PAD}
              x2={xAt(activeDay, n).toFixed(1)}
              y2={VB_H - BOT_PAD}
              stroke="currentColor"
              strokeOpacity={0.2}
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          )}

          {Array.from({ length: seriesCount }, (_, si) => {
            const color = allSeries[0][si].color;
            const pts = allSeries.map((s, i) => ({
              x: xAt(i, n),
              y: yAt(s[si].value, maxValue),
            }));
            const linePts = pts
              .map(
                ({ x, y }, i) =>
                  `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`
              )
              .join(" ");
            const fillD = [
              linePts,
              `L${xAt(n - 1, n).toFixed(1)},${baseY}`,
              `L${xAt(0, n).toFixed(1)},${baseY}`,
              "Z",
            ].join(" ");

            return (
              <g key={si}>
                <path d={fillD} fill={color} fillOpacity={0.12} />
                <path
                  d={linePts}
                  fill="none"
                  stroke={color}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            );
          })}
        </ChartSvg>
      </SvgWrapper>
      <ChartFooter days={days} legend={legend} />
    </ChartOuter>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const COLORS = {
  added: "#4ade80",
  changed: "#facc15",
  removed: "#f87171",
  sent: "#60a5fa",
  failed: "#f87171",
  usersReached: "#a78bfa",
};

export const StatsRoot = () => {
  const metrics = useMetrics(14);

  if (metrics.state === "idle" || metrics.state === "loading") {
    return (
      <NavbarProvider>
        <Page>
          <Heading>Stats</Heading>
          <StatusMessage>Loading…</StatusMessage>
        </Page>
      </NavbarProvider>
    );
  }

  if (metrics.state === "failed") {
    return (
      <NavbarProvider>
        <Page>
          <Heading>Stats</Heading>
          <StatusMessage>
            Failed to load metrics: {metrics.error.message ?? "unknown error"}
          </StatusMessage>
        </Page>
      </NavbarProvider>
    );
  }

  const days = metrics.data;

  const totals = days.reduce(
    (acc, day) => {
      acc.sent += day.notifications.sent;
      acc.failed += day.notifications.failed;
      acc.usersReached += day.notifications.usersReached;
      for (const studio of Object.values(day.diffs)) {
        acc.added += studio.added;
        acc.changed += studio.changed;
        acc.removed += studio.removed;
      }
      return acc;
    },
    { sent: 0, failed: 0, usersReached: 0, added: 0, changed: 0, removed: 0 }
  );

  const studioIds = [...new Set(days.flatMap((d) => Object.keys(d.diffs)))];

  return (
    <NavbarProvider>
      <Page>
        <Heading>Stats</Heading>

        <TotalsRow>
          <TotalCard>
            <TotalLabel>Notifications sent (14d)</TotalLabel>
            <TotalValue>{totals.sent.toLocaleString()}</TotalValue>
          </TotalCard>
          <TotalCard>
            <TotalLabel>Users reached (14d)</TotalLabel>
            <TotalValue>{totals.usersReached.toLocaleString()}</TotalValue>
          </TotalCard>
          <TotalCard>
            <TotalLabel>Delivery failures (14d)</TotalLabel>
            <TotalValue>{totals.failed.toLocaleString()}</TotalValue>
          </TotalCard>
          <TotalCard>
            <TotalLabel>Classes added (14d)</TotalLabel>
            <TotalValue>{totals.added.toLocaleString()}</TotalValue>
          </TotalCard>
          <TotalCard>
            <TotalLabel>Classes changed (14d)</TotalLabel>
            <TotalValue>{totals.changed.toLocaleString()}</TotalValue>
          </TotalCard>
          <TotalCard>
            <TotalLabel>Classes removed (14d)</TotalLabel>
            <TotalValue>{totals.removed.toLocaleString()}</TotalValue>
          </TotalCard>
        </TotalsRow>

        <Section>
          <SectionTitle>Push notifications (14 days)</SectionTitle>
          <LineChart
            days={days}
            seriesForDay={(day) => [
              { value: day.notifications.sent, color: COLORS.sent, label: "Sent" },
              { value: day.notifications.failed, color: COLORS.failed, label: "Failed" },
              { value: day.notifications.usersReached, color: COLORS.usersReached, label: "Users reached" },
            ]}
            legend={[
              { color: COLORS.sent, label: "Sent" },
              { color: COLORS.failed, label: "Failed" },
              { color: COLORS.usersReached, label: "Users reached" },
            ]}
          />
        </Section>

        {studioIds.map((studioId) => (
          <Section key={studioId}>
            <SectionTitle>
              Schedule changes — {STUDIOS[studioId]?.location ?? studioId} (14
              days)
            </SectionTitle>
            <LineChart
              days={days}
              seriesForDay={(day) => {
                const s = day.diffs[studioId] ?? {
                  added: 0,
                  changed: 0,
                  removed: 0,
                };
                return [
                  { value: s.added, color: COLORS.added, label: "Added" },
                  { value: s.changed, color: COLORS.changed, label: "Changed" },
                  { value: s.removed, color: COLORS.removed, label: "Removed" },
                ];
              }}
              legend={[
                { color: COLORS.added, label: "Added" },
                { color: COLORS.changed, label: "Changed" },
                { color: COLORS.removed, label: "Removed" },
              ]}
            />
          </Section>
        ))}
      </Page>
    </NavbarProvider>
  );
};
